import { formatFiltersForApi } from '../../utils/filterUtils';

const initiationSearchKeysDict = {
    account: 'Client Name',
    customer: 'Customer Number',
    clinician: 'Clinician Name',
    booking: 'Booking Number',
    collector: 'Collector',
    po: 'PO Number',
    expenseid: 'Expense ID',
    ptsid: 'PTS ID',
};

const managementSearchKeysDict = {
    account: 'Client Name',
    customer: 'Customer Number',
    bookingid: 'Booking Number',
    clinicianname: 'Clinician Name',
    invoicenumber: 'Invoice Number',
    collectorname: 'Collector',
    ponumber: 'PO Number',
    expenseid: 'Expense ID',
    ptsid: 'PTS ID',
};

const searchHelperTextDict = {
    expenseid: 'Complete value required for results',
    ptsid: 'Complete value required for results',
};

export const initiationSearchOptions = Object.entries(initiationSearchKeysDict).map(
    ([key, name]) => ({ name, value: key, helperText: searchHelperTextDict?.[key] })
);

export const managementSearchOptions = Object.entries(managementSearchKeysDict).map(
    ([key, name]) => ({ name, value: key, helperText: searchHelperTextDict?.[key] })
);

export const isBookingSelectable = (b) => ['PreInvoiced'].includes(b?.status);

export const generateIdForExpenseWrapper = (expenses = []) => {
    const ids = expenses?.map(({ id }) => id);
    return ids?.sort((a, b) => a - b)?.join('-');
};

/** Partial PTS visibility contexts for Invoice Initiation vs Invoice Management (AE2-6308). */
export const PARTIAL_PTS_VISIBILITY_CONTEXT = {
    Initiation: 'initiation',
    Management: 'management',
};

/**
 * Message copy for partial Processed Timesheet visibility flags.
 * BE returns booleans only; copy lives on FE. Missing flags are treated as false.
 */
export const PARTIAL_PTS_VISIBILITY_MESSAGES = {
    [PARTIAL_PTS_VISIBILITY_CONTEXT.Initiation]: [
        {
            flag: 'isPartialDueToInvoiceThroughDate',
            title: 'Partial Processed Timesheet',
            body: 'Additional items exist on this Processed Timesheet and are not included in the current results.',
        },
        {
            flag: 'isPartiallyPreInvoiced',
            title: 'Partially Pre-Invoiced',
            body: 'Some items on this Processed Timesheet have already been pre-invoiced.',
        },
        {
            flag: 'isPartiallyInvoiced',
            title: 'Partially Invoiced',
            body: 'Some work days on this Processed Timesheet have already been invoiced.',
        },
    ],
    [PARTIAL_PTS_VISIBILITY_CONTEXT.Management]: [
        {
            flag: 'isPartialProcessedTimesheet',
            title: 'Partial Processed Timesheet',
            body: 'This invoice contains only a portion of the Processed Timesheet.',
        },
        {
            flag: 'hasOtherPortionsPreInvoiced',
            title: 'Partially Pre-Invoiced',
            body: 'Other items from this Processed Timesheet have been pre-invoiced separately',
        },
        {
            flag: 'hasOtherPortionsInvoiced',
            title: 'Partially Invoiced',
            body: 'Other items from this Processed Timesheet have been invoiced separately.',
        },
    ],
};

export const getActivePartialPtsVisibilityMessages = (context, timesheet = {}) => {
    const definitions = PARTIAL_PTS_VISIBILITY_MESSAGES[context] || [];
    return definitions.filter(({ flag }) => !!timesheet?.[flag]);
};

export const hasPartialPtsVisibility = (context, timesheet = {}) =>
    getActivePartialPtsVisibilityMessages(context, timesheet).length > 0;

/** Normalize partial PTS visibility flags to explicit booleans (missing → false). */
export const normalizePartialPtsVisibilityFlags = (context, timesheet = {}) => {
    const definitions = PARTIAL_PTS_VISIBILITY_MESSAGES[context] || [];
    return definitions.reduce((flags, { flag }) => {
        flags[flag] = !!timesheet?.[flag];
        return flags;
    }, {});
};

/** OR flags across timesheets so a parent booking/invoice row can show one indicator. */
export const aggregatePartialPtsVisibilityFlags = (context, timesheets = []) => {
    const definitions = PARTIAL_PTS_VISIBILITY_MESSAGES[context] || [];
    const list = Array.isArray(timesheets) ? timesheets : [];

    return definitions.reduce((flags, { flag }) => {
        flags[flag] = list.some((timesheet) => !!timesheet?.[flag]);
        return flags;
    }, {});
};

export const wrapExpenseItems = (booking) => ({
    ...booking,
    timesheets: (booking?.timesheets || []).map((timesheet) => ({
        ...timesheet,
        ...normalizePartialPtsVisibilityFlags(PARTIAL_PTS_VISIBILITY_CONTEXT.Initiation, timesheet),
    })),
    expenses:
        booking?.expenses?.items?.length > 0 // filter out expenses with no items
            ? [
                  {
                      id: generateIdForExpenseWrapper(booking.expenses.items), // generate id for expense items wrapped based on expenses items ids
                      ...booking.expenses,
                  },
              ]
            : [],
});

export const formatFiltersForManagementApi = (filtersValues, filtersSetup) => {
    const statusOverridesForApiDict = { PreInvoiced: 'Invoiced' };

    const mapList = (arr, dict) => arr?.map((x) => (dict[x] ? dict[x] : x))?.filter(Boolean);

    const mappedFilters = {
        ...(filtersValues ?? {}),
        invoiceStatus: mapList(filtersValues?.invoiceStatus, statusOverridesForApiDict),
    };

    return formatFiltersForApi(mappedFilters, filtersSetup);
};

export const invoiceStatusEnum = {
    PreInvoiced: 'PreInvoiced',
    Invoiced: 'Invoiced',
    Errored: 'Errored',
    Abandoned: 'Abandoned',
    Outdated: 'Outdated',
};

export const invoiceStatusMap = {
    [invoiceStatusEnum.PreInvoiced]: 'Pre-Invoiced',
    [invoiceStatusEnum.Invoiced]: 'Invoiced',
    [invoiceStatusEnum.Errored]: 'Errored',
    [invoiceStatusEnum.Abandoned]: 'Abandoned',
    [invoiceStatusEnum.Outdated]: 'Outdated',
};

export const invoiceTypesEnum = {
    TimeOnly: 'TimeOnly',
    ExpenseOnly: 'ExpenseOnly',
    TimeAndExpense: 'TimeAndExpense',
    Deposits: 'Deposits',
    PermFees: 'PermFees',
    CreditMemos: 'CreditMemos',
    FinanceFees: 'FinanceFees',
};

export const invoiceTypeMap = {
    [invoiceTypesEnum.TimeOnly]: 'Timesheet',
    [invoiceTypesEnum.ExpenseOnly]: 'Expense',
    [invoiceTypesEnum.TimeAndExpense]: 'Timesheet & Expense',
    [invoiceTypesEnum.Deposits]: 'Deposits',
    [invoiceTypesEnum.PermFees]: 'Perm Fee',
    [invoiceTypesEnum.CreditMemos]: 'Credit Memo',
    [invoiceTypesEnum.FinanceFees]: 'Finance Fee',
};

export const InvoiceItemsTypeEnum = {
    Deposit: 'deposit',
    Expense: 'expense',
    ProcessedMileageEntry: 'ProcessedMileageEntry',
    ProcessedTimeEntry: 'ProcessedTimeEntry',
    Timesheet: 'timesheet',
};
